import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { 
  Search, 
  Filter,
  Package,
  Sofa,
  Bike,
  Gamepad2,
  Home,
  Box,
  Loader2
} from "lucide-react";

const categoryIcons = {
  muebles: Sofa,
  electrodomesticos: Box,
  deporte: Bike,
  juguetes: Gamepad2,
  hogar: Home,
  otros: Package
};

const conditionLabels = {
  nuevo: { label: "Nuevo", color: "bg-green-100 text-green-700" },
  bueno: { label: "Buen estado", color: "bg-blue-100 text-blue-700" },
  aceptable: { label: "Aceptable", color: "bg-yellow-100 text-yellow-700" }
};

const CatalogPage = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchCategories();
    fetchProducts();
  }, [selectedCategory]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data.categories);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ status: "listed" });
      if (selectedCategory) {
        params.append("category", selectedCategory);
      }
      const response = await axios.get(`${API}/products?${params}`);
      setProducts(response.data);
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const CategoryIcon = ({ category }) => {
    const Icon = categoryIcons[category] || Package;
    return <Icon className="w-5 h-5" />;
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-[#374B67] mb-2" data-testid="catalog-title">
            Catálogo de Productos
          </h1>
          <p className="text-slate-600">
            Descubre objetos disponibles y reserva los que te interesen
          </p>
        </div>

        {/* Search and Filters */}
        <div className="mb-8 space-y-4">
          {/* Search Bar */}
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              type="text"
              placeholder="Buscar productos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-12 rounded-full border-slate-200"
              data-testid="search-input"
            />
          </div>

          {/* Category Filters */}
          <div className="flex flex-wrap gap-2">
            <Button
              variant={selectedCategory === null ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedCategory(null)}
              className={`rounded-full ${selectedCategory === null ? "bg-[#374B67]" : ""}`}
              data-testid="filter-all"
            >
              Todos
            </Button>
            {categories.map((cat) => (
              <Button
                key={cat.id}
                variant={selectedCategory === cat.id ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedCategory(cat.id)}
                className={`rounded-full ${selectedCategory === cat.id ? "bg-[#374B67]" : ""}`}
                data-testid={`filter-${cat.id}`}
              >
                <CategoryIcon category={cat.id} />
                <span className="ml-2">{cat.name}</span>
              </Button>
            ))}
          </div>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#374B67]" />
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-20">
            <Package className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-xl font-semibold text-slate-600 mb-2">No hay productos disponibles</h3>
            <p className="text-slate-500">
              {searchTerm ? "Prueba con otros términos de búsqueda" : "Vuelve pronto para ver nuevos productos"}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredProducts.map((product) => (
              <Link to={`/producto/${product.product_id}`} key={product.product_id}>
                <Card className="product-card group bg-white border-0 shadow-sm hover:shadow-xl overflow-hidden h-full" data-testid={`product-card-${product.product_id}`}>
                  {/* Image */}
                  <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
                    <img
                      src={product.image_url || "https://via.placeholder.com/400x300?text=Sin+imagen"}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    {/* Condition Badge */}
                    <Badge className={`absolute top-3 left-3 ${conditionLabels[product.condition]?.color || "bg-slate-100"}`}>
                      {conditionLabels[product.condition]?.label || product.condition}
                    </Badge>
                    {/* Points Badge */}
                    <div className="absolute bottom-3 right-3 bg-white/90 backdrop-blur-sm rounded-full px-3 py-1 text-sm font-semibold text-[#374B67]">
                      {product.points_cost} Pts
                    </div>
                  </div>
                  
                  {/* Content */}
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h3 className="font-semibold text-[#374B67] line-clamp-1 group-hover:text-[#69C7EA] transition-colors">
                        {product.name}
                      </h3>
                      <CategoryIcon category={product.category} />
                    </div>
                    <p className="text-sm text-slate-500 line-clamp-2 mb-3">
                      {product.description}
                    </p>
                    <div className="flex items-center justify-between text-xs text-slate-400">
                      <span>Por {product.owner_name}</span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default CatalogPage;
